import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getShareByToken, incrementShareView, type ShareRow, type ShareItem } from '@/lib/zcall';
import { md5 } from '@/lib/md5';
import DeckViewer from '@/components/feature/presentation/DeckViewer';
import type { SlideDeck } from '@/lib/slideDeck';

const BRAND_LOGO =
  'https://storage.helloreaddy.io/project_files/59392c9e-e303-496e-bac1-59ba5941cf65/07452721-5131-4b23-922e-acc73d68a0a9_compressed_zifek.webp';

interface SheetLike {
  name?: string;
  cells?: Record<string, string>;
  rows?: number;
  cols?: number;
  formats?: Record<string, { bold?: boolean; italic?: boolean; color?: string; bg?: string; align?: 'left' | 'center' | 'right'; fontSize?: number }>;
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Rendu lisible d'une feuille de calcul (aperçu, sans édition). */
function ExcelPreview({ content, name }: { content: Record<string, unknown>; name: string }) {
  const sheets = useMemo<SheetLike[]>(() => {
    const list = Array.isArray(content.sheets) ? (content.sheets as SheetLike[]) : [];
    if (list.length > 0) return list;
    return [
      {
        name: 'Feuille 1',
        cells: (content.cells as Record<string, string>) || {},
        rows: Number(content.rows) || 24,
        cols: Number(content.cols) || 8,
        formats: (content.formats as SheetLike['formats']) || {},
      },
    ];
  }, [content]);

  const [active, setActive] = useState(0);
  const sheet = sheets[Math.min(active, sheets.length - 1)] || sheets[0];
  const cells = sheet?.cells || {};
  const formats = sheet?.formats || {};

  // Bornage : on n'affiche que la zone réellement utilisée (max 80 × 20).
  const bounds = useMemo(() => {
    let maxRow = 0;
    let maxCol = 0;
    Object.keys(cells).forEach((key) => {
      const [r, c] = key.split(',').map(Number);
      if (!cells[key]) return;
      if (Number.isFinite(r)) maxRow = Math.max(maxRow, r);
      if (Number.isFinite(c)) maxCol = Math.max(maxCol, c);
    });
    return {
      rows: Math.min(80, Math.max(6, maxRow + 2)),
      cols: Math.min(20, Math.max(4, maxCol + 2)),
    };
  }, [cells]);

  return (
    <div className="space-y-3">
      {sheets.length > 1 && (
        <div className="flex flex-wrap gap-1.5">
          {sheets.map((s, i) => (
            <button
              key={`${s.name || 'feuille'}-${i}`}
              onClick={() => setActive(i)}
              className={`cursor-pointer rounded-full px-3 py-1.5 text-xs font-medium whitespace-nowrap ${
                i === active
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200/70'
              }`}
            >
              {s.name || `Feuille ${i + 1}`}
            </button>
          ))}
        </div>
      )}
      <div className="overflow-auto rounded-lg border border-background-200/70 bg-white">
        <table className="border-collapse text-xs" aria-label={`Aperçu de ${name}`}>
          <tbody>
            {Array.from({ length: bounds.rows }).map((_, r) => (
              <tr key={r}>
                <th className="sticky left-0 z-10 w-9 border border-background-200/60 bg-background-100 px-1.5 py-1 text-[10px] font-medium text-foreground-400">
                  {r + 1}
                </th>
                {Array.from({ length: bounds.cols }).map((__, c) => {
                  const key = `${r},${c}`;
                  const value = cells[key] || '';
                  const fmt = formats[key] || {};
                  return (
                    <td
                      key={c}
                      className="max-w-[260px] truncate border border-background-200/60 px-2 py-1 text-foreground-800"
                      style={{
                        textAlign: fmt.align || 'left',
                        fontWeight: fmt.bold ? 600 : 400,
                        fontStyle: fmt.italic ? 'italic' : 'normal',
                        background: fmt.bg || undefined,
                        color: fmt.color || undefined,
                      }}
                    >
                      {value}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Anciennes présentations (titre + corps) rendues comme des diapositives. */
function LegacySlidesPreview({ content }: { content: Record<string, unknown> }) {
  const slides = Array.isArray(content.slides)
    ? (content.slides as { title?: string; body?: string }[])
    : [];
  if (slides.length === 0) return <p className="text-sm text-foreground-500">Aucune diapositive.</p>;
  return (
    <div className="space-y-6">
      {slides.map((s, i) => (
        <article
          key={i}
          className="rounded-xl border border-background-200/70 bg-white p-6 md:p-8 min-h-[220px]"
        >
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-foreground-300">
            Diapositive {i + 1}
          </p>
          <h2 className="mb-3 text-lg font-semibold text-foreground-950">{s.title || ''}</h2>
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground-700">{s.body || ''}</p>
        </article>
      ))}
    </div>
  );
}

function itemIcon(type: string): string {
  if (type === 'excel') return 'ri-file-excel-2-line text-emerald-600';
  if (type === 'pdf') return 'ri-file-pdf-2-line text-red-500';
  if (type === 'presentation' || type === 'powerpoint') return 'ri-file-ppt-2-line text-orange-500';
  return 'ri-file-word-2-line text-teal-600';
}

/**
 * Page publique de visualisation des documents partagés.
 * Accepte les liens `/preview/<token>` et les anciens liens
 * `/preview/<uuid>/<id>/partage/<token>` : le token est toujours le dernier
 * segment de l'URL. Aucun compte n'est requis, aucune modification possible.
 */
export default function DocumentPreviewPage() {
  const location = useLocation();
  const token = useMemo(() => {
    const parts = location.pathname.split('/').filter(Boolean);
    const last = parts[parts.length - 1] || '';
    return last === 'preview' ? '' : last;
  }, [location.pathname]);

  const [loading, setLoading] = useState(true);
  const [share, setShare] = useState<ShareRow | null>(null);
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState('');
  const [wrong, setWrong] = useState(false);
  const [active, setActive] = useState(0);
  const viewedRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const row = await getShareByToken(token);
        if (!alive) return;
        setShare(row);
        if (!row || !row.password_hash) setUnlocked(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  useEffect(() => {
    if (!unlocked || !share || viewedRef.current) return;
    viewedRef.current = true;
    void incrementShareView(share.id);
  }, [unlocked, share]);

  const submitPassword = () => {
    if (!share?.password_hash) {
      setUnlocked(true);
      return;
    }
    if (md5(password.trim()) === share.password_hash) {
      setUnlocked(true);
      setWrong(false);
    } else {
      setWrong(true);
    }
  };

  const items: ShareItem[] = share?.content || [];
  const current = items[Math.min(active, items.length - 1)] || null;

  useEffect(() => {
    if (active > items.length - 1) setActive(0);
  }, [items.length, active]);

  const renderBody = () => {
    if (!current) return null;
    const content = current.content;

    if (current.type === 'presentation' && current.deck) {
      return <DeckViewer deck={current.deck as SlideDeck} title={current.name} />;
    }

    if (current.type === 'pdf' && current.pdfBase64) {
      return (
        <div className="space-y-3">
          <iframe
            title={current.name}
            src={`data:application/pdf;base64,${current.pdfBase64}`}
            className="h-[74vh] w-full rounded-lg border border-background-200/70 bg-white"
          />
          <a
            href={`data:application/pdf;base64,${current.pdfBase64}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-4 py-2 text-xs font-semibold text-background-50 hover:bg-primary-600 whitespace-nowrap no-underline"
          >
            <i className="ri-fullscreen-line"></i>
            Ouvrir en plein écran
          </a>
        </div>
      );
    }

    if (current.type === 'excel' && content) {
      return <ExcelPreview content={content} name={current.name} />;
    }

    if ((current.type === 'powerpoint' || current.type === 'presentation') && content) {
      return <LegacySlidesPreview content={content} />;
    }

    if (current.html) {
      return (
        <div className="rounded-xl border border-background-200/70 bg-white p-6 md:p-10">
          <div
            className="prose max-w-none overflow-x-auto text-sm leading-relaxed text-foreground-800"
            dangerouslySetInnerHTML={{ __html: current.html }}
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-background-200/70 bg-background-100 py-16 text-center">
        <i className="ri-file-unknow-line text-3xl text-foreground-300"></i>
        <p className="text-sm text-foreground-600">Aperçu non disponible pour ce document.</p>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-50">
        <div className="flex items-center gap-2 text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i>
          <span className="text-sm">Ouverture du document…</span>
        </div>
      </div>
    );
  }

  if (!share) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-50 px-4">
        <div className="w-full max-w-sm text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-background-100">
            <i className="ri-link-unlink-m text-2xl text-foreground-400"></i>
          </div>
          <h1 className="mb-2 text-base font-semibold text-foreground-900">Document introuvable</h1>
          <p className="text-sm text-foreground-500">
            Ce lien de consultation est invalide ou a été supprimé par son propriétaire.
          </p>
          <Link to="/" className="mt-5 inline-block text-xs font-semibold text-primary-600 no-underline">
            Retour sur Zifek
          </Link>
        </div>
      </div>
    );
  }

  if (!unlocked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-50 px-4">
        <div className="w-full max-w-sm rounded-2xl border border-background-200/70 bg-background-50 p-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-accent-50">
            <i className="ri-lock-line text-2xl text-accent-600"></i>
          </div>
          <h1 className="mb-1 text-base font-semibold text-foreground-900">
            {share.title || 'Document protégé'}
          </h1>
          <p className="mb-5 text-xs text-foreground-500">
            Ce document est protégé par un mot de passe.
          </p>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitPassword();
            }}
            placeholder="Mot de passe"
            className="mb-3 w-full rounded-xl border border-background-200/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-800 focus:border-accent-300 focus:outline-none"
          />
          {wrong && <p className="mb-3 text-xs text-red-500">Mot de passe incorrect.</p>}
          <button
            onClick={submitPassword}
            className="w-full cursor-pointer rounded-full bg-accent-500 py-3 text-sm font-semibold text-background-50 hover:bg-accent-600 whitespace-nowrap"
          >
            Consulter le document
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <header className="sticky top-0 z-30 border-b border-background-200/70 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1200px] items-center gap-3 px-4 py-3 md:px-6">
          <span className="h-8 w-8 flex-shrink-0 overflow-hidden rounded-lg">
            <img src={BRAND_LOGO} alt="ZIFEK" title="ZIFEK" className="h-full w-full object-cover" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-foreground-950">
              {share.title || 'Documents partagés'}
            </p>
            <p className="text-[11px] text-foreground-500">
              {items.length} document{items.length !== 1 ? 's' : ''} · consultation seule
            </p>
          </div>
          <span className="hidden items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1.5 text-[11px] font-medium text-secondary-800 sm:inline-flex whitespace-nowrap">
            <i className="ri-eye-line"></i>
            Lecture seule
          </span>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1200px] flex-col gap-5 px-4 py-5 md:px-6 lg:flex-row">
        {items.length > 1 && (
          <aside className="w-full flex-shrink-0 lg:w-64">
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-foreground-400">
              Documents
            </p>
            <div className="flex gap-1.5 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible">
              {items.map((it, i) => (
                <button
                  key={`${it.name}-${i}`}
                  onClick={() => setActive(i)}
                  className={`flex flex-shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-left text-xs transition-colors cursor-pointer lg:w-full ${
                    i === active
                      ? 'bg-primary-50 font-semibold text-primary-700'
                      : 'text-foreground-700 hover:bg-background-100'
                  }`}
                  title={it.name}
                >
                  <i className={itemIcon(it.type)}></i>
                  <span className="max-w-[180px] truncate lg:max-w-none">{it.name}</span>
                </button>
              ))}
            </div>
          </aside>
        )}

        <main className="min-w-0 flex-1">
          <div className="mb-3 flex items-start justify-between gap-3">
            <h1 className="min-w-0 break-words text-base font-semibold text-foreground-950">
              {current?.name || share.title}
            </h1>
            <span className="hidden flex-shrink-0 items-center gap-1 text-[11px] text-foreground-400 sm:inline-flex whitespace-nowrap">
              <i className="ri-lock-2-line"></i>
              Non modifiable
            </span>
          </div>
          {renderBody()}
          <p className="mt-6 text-center text-[11px] text-foreground-400">
            Document partagé via Zifek — {items.length} fichier{items.length !== 1 ? 's' : ''} · aucune
            modification possible
          </p>
        </main>
      </div>
    </div>
  );
}